import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Button, Flex } from '@mantine/core';
import { ProfileBox } from '@dealstack/frontend/shared/components/ProfileBox/ProfileBox';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { MiniListCard } from '@dealstack/frontend/shared/components/list/MiniListCard/MiniListCard';
import { ORGANIZATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { useMutation } from '@tanstack/react-query';
import {
	updateOrganization,
	updateOrganizationType,
} from '@dealstack/frontend/shared/dataExchange/services/organization.api';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { OrganizationRes, OrganizationType } from '@dealstack/shared/lib/generated';
import { useOrganizationUsers } from '@dealstack/frontend/app/pages/Organization/hooks/useOrganizationUsers';
import { InviteTeamMemberDrawer } from '@dealstack/frontend/shared/components/InviteTeamMemberDrawer/InviteTeamMemberDrawer';
import {
	FGAInternalRole,
	getResourceTypePermissions,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import {
	UPDATE_ORGANIZATION_FORM_FIELDS,
	UpdateOrganizationSettingsInputsValues,
} from '@dealstack/frontend/app/pages/Organization/OrganizationGeneralSettings/OrganizationGeneralSettings.types';
import { useState } from 'react';
import { OrganizationForm } from '@dealstack/frontend/app/pages/Organization/OrganizationGeneralSettings/OrganizationForm';
import { StyledFormWrapper } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeGeneralSettings/programmeGeneralSettings.style';
import { formatUserFullName } from '@dealstack/shared/lib/format/user';
import { useOrganization } from '@dealstack/frontend/app/pages/Organization/hooks/useOrganization';
import { useInviteOrganizationUser } from '@dealstack/frontend/app/pages/Organization/hooks/useOrganizationInviteUser';
import { OrganizationSettingsView } from '@dealstack/frontend/app/pages/Organization/Organization.types';

interface Props {
	organization: OrganizationRes;
	onViewChange: (view: OrganizationSettingsView) => void;
}

export const OrganizationGeneralSettings = ({ organization, onViewChange }: Props) => {
	const tOrganization = useTranslationOrganization();
	const [showInviteDrawer, setShowInviteDrawer] = useState(false);

	const updateOrganizationMutation = useMutation({
		mutationFn: updateOrganization,
	});
	const updateOrganizationTypeMutation = useMutation({
		mutationFn: updateOrganizationType,
	});

	const { value: programmeDefaultsEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.PROGRAMME_DEFAULTS,
		organization.id ? { organizationId: organization.id } : {}
	);
	// Support-gated the same way the Funds section is. Org admins see the classification but
	// cannot grant themselves the Programme Defaults capability.
	const { hasPermission: hasSupportPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: '',
		permissionRole: FGAInternalRole.DEALSTACK_SUPPORT,
	});
	const canEditType = programmeDefaultsEnabled && hasSupportPermission;
	const { data: users, refetch: refetchOrganizationUsers } = useOrganizationUsers({
		organizationId: organization.id,
	});
	const { refetch: refetchOrganization } = useOrganization(organization.id, {
		enabled: false,
	});

	const { mutate: inviteOrganizationUser, isPending } = useInviteOrganizationUser();

	const handleOrganizationInvite = ({ firstName, lastName, email, role }, { onSuccess }) => {
		if (!organization.id) {
			showToastV2({ state: 'error', message: 'Missing organisation id' });
			return;
		}

		inviteOrganizationUser(
			{
				organizationId: organization.id,
				firstName,
				lastName,
				email,
				permissionRoles: [role],
			},
			{
				onSuccess: async () => {
					await refetchOrganizationUsers();
					onSuccess?.();
				},
			}
		);
	};

	const handleUpdateOrganization = async (formData: UpdateOrganizationSettingsInputsValues) => {
		if (!organization.id) {
			showToastV2({ state: 'error', message: 'Missing organisation id' });
			return;
		}

		const selectedType = formData[UPDATE_ORGANIZATION_FORM_FIELDS.TYPE];
		const nextType: OrganizationType | null = selectedType ? selectedType : null;
		// Two endpoints because the classification is support-only. Skipped unless it actually
		// changed, so an org admin saving name/email never touches it.
		const shouldUpdateType = canEditType && nextType !== (organization.type ?? null);

		try {
			await updateOrganizationMutation.mutateAsync({
				id: organization.id,
				body: {
					name: formData[UPDATE_ORGANIZATION_FORM_FIELDS.NAME],
					contactEmail: formData[UPDATE_ORGANIZATION_FORM_FIELDS.CONTACT_EMAIL],
				},
			});

			if (shouldUpdateType) {
				await updateOrganizationTypeMutation.mutateAsync({
					organizationId: organization.id,
					type: nextType,
				});
			}

			showToastV2();
			await refetchOrganization();
		} catch {
			showToastV2({ state: 'error' });
		}
	};

	const organizationRelations = getResourceTypePermissions(ResourceType.ORGANIZATION);

	const permissions = organizationRelations.map((relation) => ({
		label: tOrganization(`invite_team_member.relation.${relation}`),
		value: relation,
	}));

	const mappedUsers =
		users?.map((user) => ({
			id: user.id,
			value: {
				top: formatUserFullName(user),
				bottom: user.email,
			},
		})) ?? [];

	return (
		<>
			<Flex w="100%" justify="space-between">
				<StyledFormWrapper align="flex-start" p="1rem">
					<OrganizationForm
						name={organization.name}
						contactEmail={organization.contactEmail ?? ''}
						type={organization.type ?? null}
						showType={programmeDefaultsEnabled}
						canEditType={canEditType}
						isLoading={
							updateOrganizationMutation.isPending ||
							updateOrganizationTypeMutation.isPending
						}
						handleUpdateOrganization={handleUpdateOrganization}
					/>
				</StyledFormWrapper>

				<Flex direction="column" gap="1rem" p="1rem" className="w-4/12 p-4">
					<ProfileBox
						icon={<Icon variant="building-office-company-entity-line" size="xxl" />}
						text={organization.name}
					/>
					<MiniListCard
						title={tOrganization('settings.members')}
						items={mappedUsers}
						moreItemsClick={() => onViewChange(OrganizationSettingsView.Members)}
						children={
							<Button
								mt="1rem"
								ml="0.5rem"
								onClick={() => setShowInviteDrawer(true)}
								data-testid={
									ORGANIZATION_ELEMENT_ID.MINI_LIST_CARD_INVITE_USER_BUTTON
								}
							>
								{tOrganization('invite_user')}
							</Button>
						}
					/>
				</Flex>
			</Flex>
			{organization.id && (
				<InviteTeamMemberDrawer
					isOpen={showInviteDrawer}
					setIsOpen={setShowInviteDrawer}
					onInvite={handleOrganizationInvite}
					isLoading={isPending}
					permissions={permissions}
					description={tOrganization('invite_member_drawer.description')}
					roleLabel={tOrganization('invite_member_drawer.form.role_field.label')}
				/>
			)}
		</>
	);
};
